import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';

import './Notification.css';

export type NotificationConfig = {
  /**
   * Determines where in the page the notification should be displayed
   * @default "topRight"
   */
  placement?: 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft';
  /**
   * Number of seconds that the notification appears for
   * @default 5
   */
  duration?: number;
  /** The main message which appears in the notification */
  message: string;
  /** The description which appears beneath the message */
  description?: string;
  /**
   * The type of notification
   * @default "info"
   */
  status?: 'success' | 'error' | 'warning' | 'info';
  /** When set, this callback will be used to call functionality when the notification is closed */
  onClose?: () => void;
  /** Unique identifier for the notification. If not set, defaults to a random string */
  id?: string;
};

export interface INotificationProps {
  config?: NotificationConfig;
}

const icon: { [key: string]: React.ReactNode } = {
  success: <CheckCircleIcon />,
  error: <XCircleIcon />,
  warning: <ExclamationCircleIcon />,
  info: <InformationCircleIcon />,
};

export const Notification: React.FC<
  React.PropsWithChildren<INotificationProps>
> = ({ config }) => {
  const {
    message,
    duration = 5,
    description,
    status = 'info',
    onClose,
  } = config || {};

  useEffect(() => {
    const timer = setTimeout(() => onClose?.(), duration * 1000);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div className={`notification notification-${status}`} role="status" aria-live="polite">
      <span className="notification-icon">{icon[status]}</span>
      <div className="notification-content">
        <div className="notification-message">
          <span>{message}</span>
        </div>
        {description && (
          <span className="notification-description">{description}</span>
        )}
      </div>
      <button type="button" onClick={onClose} className="notification-close">
        <span className="sr-only">Close notification</span>
        <XMarkIcon aria-hidden="true" />
      </button>
    </div>
  );
};
